import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiMinus, FiPlus, FiTrash2 } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import siteConfig, { formatPrice } from "../config/siteConfig";

const CartPage = () => {
  const navigate = useNavigate();
  const [promo, setPromo] = useState("");
  const [promoMessage, setPromoMessage] = useState("");
  const { cartItems, removeFromCart, updateQuantity, clearCart, cartCount } =
    useContext(CartContext);

  const lines = cartItems
    .map((item) => ({
      ...item,
      product: productData.find((product) => product.id === item.id),
    }))
    .filter((item) => item.product);
  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping = subtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.standardShipping;
  const total = subtotal + shipping;
  const remaining = Math.max(0, siteConfig.freeShippingThreshold - subtotal);
  const progress = Math.min(100, (subtotal / siteConfig.freeShippingThreshold) * 100);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const checkout = () => {
    navigate("/checkout");
    scrollTop();
  };

  const applyPromo = (event) => {
    event.preventDefault();
    setPromoMessage(
      promo.trim().toUpperCase() === "CIRCULAR"
        ? "Code herkend. Kortingen worden tijdens de campagne automatisch toegepast."
        : "Deze code is niet geldig voor de huidige collectie."
    );
  };

  if (lines.length === 0) {
    return (
      <main className="cart-empty page-shell app-page-enter">
        <p className="eyebrow">Shopping bag / 00</p>
        <h1>Je shopping bag is leeg.</h1>
        <p>Een goede broek wacht niet in je winkelmand. Vind jouw perfecte fit.</p>
        <Link className="solid-button" to="/shop" onClick={scrollTop}>
          Ontdek de collectie <FiArrowRight aria-hidden="true" />
        </Link>
      </main>
    );
  }

  return (
    <main className="cart-page app-page-enter">
      <header className="cart-page__header">
        <p className="eyebrow">Your selection</p>
        <h1>Shopping bag <span>{String(cartCount).padStart(2, "0")}</span></h1>
        <button type="button" onClick={clearCart}>Maak leeg</button>
      </header>

      <div className="cart-page__layout">
        <section className="cart-list" aria-label="Shopping bag items">
          <div className="cart-progress">
            <p>
              {remaining > 0
                ? `Nog ${formatPrice(remaining)} tot gratis verzending.`
                : "Je bestelling wordt gratis verzonden."}
            </p>
            <div><span style={{ width: `${progress}%` }} /></div>
          </div>

          {lines.map((item) => (
            <article className="cart-line" key={`${item.id}-${item.size}-${item.color}`}>
              <Link className="cart-line__image" to={`/shopDetails/${item.id}`} onClick={scrollTop}>
                <img src={item.product.image} alt={item.product.name} />
              </Link>
              <div className="cart-line__info">
                <p>{item.product.category} / {item.product.attributes.fit} fit</p>
                <Link to={`/shopDetails/${item.id}`} onClick={scrollTop}>
                  <h2>{item.product.name}</h2>
                </Link>
                <dl>
                  <div><dt>Kleur</dt><dd>{item.color}</dd></div>
                  <div><dt>Maat</dt><dd>{item.size}</dd></div>
                </dl>
                <button
                  type="button"
                  className="cart-line__remove"
                  onClick={() => removeFromCart(item.id, item.size, item.color)}
                >
                  <FiTrash2 aria-hidden="true" /> Verwijder
                </button>
              </div>
              <div className="cart-line__price">
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
                <div className="cart-line__quantity">
                  <button
                    type="button"
                    onClick={() =>
                      item.quantity === 1
                        ? removeFromCart(item.id, item.size, item.color)
                        : updateQuantity(item.id, item.size, item.color, item.quantity - 1)
                    }
                    aria-label="Decrease quantity"
                  >
                    <FiMinus aria-hidden="true" />
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                    aria-label="Increase quantity"
                  >
                    <FiPlus aria-hidden="true" />
                  </button>
                </div>
              </div>
            </article>
          ))}

          <Link className="text-link cart-list__continue" to="/shop" onClick={scrollTop}>
            Verder winkelen <FiArrowRight aria-hidden="true" />
          </Link>
        </section>

        <aside className="cart-summary">
          <p className="eyebrow">Order summary</p>
          <div className="cart-summary__row"><span>Subtotaal</span><strong>{formatPrice(subtotal)}</strong></div>
          <div className="cart-summary__row"><span>Verzending</span><strong>{shipping === 0 ? "Gratis" : formatPrice(shipping)}</strong></div>
          <div className="cart-summary__total"><span>Totaal</span><strong>{formatPrice(total)}</strong></div>
          <p className="cart-summary__tax">Inclusief btw. Levering en kortingen worden bevestigd bij checkout.</p>
          <button className="solid-button" type="button" onClick={checkout}>
            Naar checkout <FiArrowRight aria-hidden="true" />
          </button>

          <form className="cart-summary__promo" onSubmit={applyPromo}>
            <label htmlFor="promo-code">Promocode</label>
            <div>
              <input
                id="promo-code"
                value={promo}
                onChange={(event) => setPromo(event.target.value)}
                placeholder="Vul code in"
              />
              <button type="submit">Apply</button>
            </div>
            {promoMessage && <p role="status">{promoMessage}</p>}
          </form>

          <div className="cart-summary__service">
            <h3>Need a hand?</h3>
            <p>We helpen je graag met maat, pasvorm of levering.</p>
            <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
